import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { formatCents, formatDateTime, formatDay, formatInt, formatWhen } from "@/lib/format";
import { exportSaved, listSaved, PRESETS, runSaved, saveReport, type Column, type RunChoices, type SavedReport } from "@/lib/reportsApi";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { LoadError } from "@/components/LoadError";

const GROUPS: [string, string][] = [
  ["sales", "Sales and money due"],
  ["events", "Events"],
  ["contacts", "Contacts"],
  ["email", "Email"],
  ["contact_lists", "Contact lists (from Keap)"],
];
const BRANDS: [number, string][] = [
  [1, "CREU"],
  [2, "MHU"],
  [3, "RVU"],
  [4, "SSU"],
  [5, "Billboard (OBU)"],
];
const muted = "text-[var(--color-muted-foreground)]";
const link = "text-[var(--color-primary)] hover:underline";

export type SavedReportsSearch = { report?: number; due?: "upcoming" | "past_due" };

/** Reports: Brandon's saved reports (his Keap saved searches), run for any dates and contact list, with CSV. */
export function SavedReportsPage() {
  const { report, due } = useSearch({ from: "/reports" }) as SavedReportsSearch;
  const navigate = useNavigate({ from: "/reports" });
  const list = useQuery({ queryKey: ["reports-saved"], queryFn: listSaved });
  const segments = useQuery({ queryKey: ["segments-reports"], queryFn: () => api<{ segments: { id: number; name: string; last_count: number | null }[] }>("/api/v1/segments?limit=500").then((r) => r.segments ?? []) });
  const searches = useQuery({ queryKey: ["saved-searches-reports"], queryFn: () => api<{ saved_searches: { id: number; name: string }[] }>("/api/v1/saved-searches").then((r) => r.saved_searches ?? []) });
  const reports = list.data?.reports ?? [];
  const selected = reports.find((r) => r.id === report) ?? reports[0];

  return (
    <div className="mx-auto max-w-[1300px]">
      <PageHeader title="Saved reports" sub="Your Keap saved searches, run for any dates. Mountain Time." />
      {list.isError && <LoadError className="px-0" what="saved reports" error={list.error} onRetry={() => list.refetch()} />}
      <div className="grid gap-3 lg:grid-cols-[17rem_1fr]">
        <nav aria-label="Saved reports" className="space-y-3 text-sm">
          {GROUPS.map(([cat, title]) => {
            const items = reports.filter((r) => r.category === cat);
            if (items.length === 0) return null;
            return (
              <div key={cat}>
                <h2 className={`mb-1 px-2 text-xs font-medium uppercase tracking-wide ${muted}`}>{title}</h2>
                {items.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => navigate({ search: { report: r.id }, replace: true })}
                    aria-current={selected?.id === r.id ? "page" : undefined}
                    className={`flex w-full items-center gap-2 rounded-md px-2 py-1 text-left hover:bg-[var(--color-muted)] ${selected?.id === r.id ? "bg-[var(--color-muted)] font-medium" : ""}`}
                  >
                    <span className="min-w-0 flex-1 truncate">{r.name}</span>
                    {r.home_group && <Badge variant="muted">home</Badge>}
                  </button>
                ))}
              </div>
            );
          })}
          <div>
            <h2 className={`mb-1 px-2 text-xs font-medium uppercase tracking-wide ${muted}`}>Your segments</h2>
            {segments.error && <LoadError className="px-2 py-1 text-xs" what="segments" error={segments.error} onRetry={() => segments.refetch()} />}
            {segments.isSuccess && segments.data.length === 0 && <p className={`px-2 text-xs ${muted}`}>None yet.</p>}
            {(segments.data ?? []).map((s) => (
              <Link key={s.id} to="/email/segments/$segmentId" params={{ segmentId: String(s.id) }} className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-[var(--color-muted)]">
                <span className="min-w-0 flex-1 truncate text-[var(--color-primary)]">{s.name}</span>
                {s.last_count != null && <span className={`num text-xs ${muted}`}>{formatInt(s.last_count)}</span>}
              </Link>
            ))}
          </div>
          <div>
            <h2 className={`mb-1 px-2 text-xs font-medium uppercase tracking-wide ${muted}`}>Saved contact searches</h2>
            {searches.error && <LoadError className="px-2 py-1 text-xs" what="saved searches" error={searches.error} onRetry={() => searches.refetch()} />}
            {searches.isSuccess && searches.data.length === 0 && <p className={`px-2 text-xs ${muted}`}>None yet.</p>}
            {(searches.data ?? []).map((s) => (
              <Link key={s.id} to="/contacts" search={{ saved: s.id }} className="block truncate rounded-md px-2 py-1 text-[var(--color-primary)] hover:bg-[var(--color-muted)]">
                {s.name}
              </Link>
            ))}
          </div>
        </nav>
        {selected ? (
          <ReportRunner
            key={`${selected.id}:${due ?? ""}`}
            report={selected}
            due={selected.kind === "receivables" ? due : undefined}
            events={list.data?.events ?? []}
            segments={segments.data ?? []}
            searches={searches.data ?? []}
          />
        ) : (
          list.isLoading && <p className={muted}>Loading…</p>
        )}
      </div>
    </div>
  );
}

function ReportRunner({
  report,
  due,
  events,
  segments,
  searches,
}: {
  report: SavedReport;
  /** Money due: open filtered to still upcoming or past due (from the home page's numbers). */
  due?: string;
  events: { event_id: number; event_name: string; session_id: number; starts_at: string; upcoming: boolean }[];
  segments: { id: number; name: string }[];
  searches: { id: number; name: string }[];
}) {
  const qc = useQueryClient();
  const [choices, setChoices] = useState<RunChoices>({ preset: "", due });
  const [exporting, setExporting] = useState("");
  const tags = useQuery({ queryKey: ["tags-picker"], queryFn: () => api<{ tags: { id: number; name: string }[] }>("/api/v1/tags").then((r) => r.tags ?? []), staleTime: 300_000 });
  const set = (p: Partial<RunChoices>) => setChoices((c) => ({ ...c, ...p }));
  const customReady = choices.preset !== "custom" || (!!choices.from && !!choices.to);
  const run = useQuery({ queryKey: ["reports-run", report.id, choices], queryFn: () => runSaved(report.id, choices), enabled: customReady, placeholderData: (prev) => prev });
  const home = useMutation({
    mutationFn: (g: "sales" | "company" | "none") => saveReport(report, g),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["reports-saved"] });
      void qc.invalidateQueries({ queryKey: ["reports-home"] });
    },
  });
  const isList = report.kind === "contact_list";
  const isEvent = report.kind === "event_revenue";
  const r = run.data;
  const download = async () => {
    setExporting("Preparing the file…");
    try {
      await exportSaved(report.id, choices, report.name);
      setExporting("");
    } catch (e) {
      setExporting(`Couldn't download: ${(e as Error).message}`);
    }
  };

  return (
    <div className="min-w-0 space-y-3">
      <Card>
        <CardHeader>
          <div className="min-w-0">
            <CardTitle>{report.name}</CardTitle>
            <p className={`mt-0.5 text-sm ${muted}`}>
              {report.description}
              {report.keap_id ? ` Keap saved search ${report.keap_id}.` : ""}
            </p>
          </div>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3 text-sm">
          {!isList && !isEvent && (
            <label className="space-y-1">
              <span className={`block text-xs ${muted}`}>Dates</span>
              <Select aria-label="Dates" value={choices.preset} onChange={(e) => set({ preset: e.target.value })} className="h-8 w-48">
                <option value="">{report.window_label || "The report's own dates"}</option>
                {PRESETS.map((p) => (
                  <option key={p.preset} value={p.preset}>
                    {p.label}
                  </option>
                ))}
              </Select>
            </label>
          )}
          {choices.preset === "custom" && (
            <>
              <label className="space-y-1">
                <span className={`block text-xs ${muted}`}>From</span>
                <Input type="date" aria-label="From" value={choices.from ?? ""} onChange={(e) => set({ from: e.target.value })} className="h-8 w-40" />
              </label>
              <label className="space-y-1">
                <span className={`block text-xs ${muted}`}>To</span>
                <Input type="date" aria-label="To" value={choices.to ?? ""} onChange={(e) => set({ to: e.target.value })} className="h-8 w-40" />
              </label>
            </>
          )}
          {isEvent && (
            <label className="space-y-1">
              <span className={`block text-xs ${muted}`}>Event date</span>
              <Select aria-label="Event date" value={choices.session_id ?? 0} onChange={(e) => set({ session_id: Number(e.target.value) || undefined })} className="h-8 w-72">
                <option value={0}>The next date (picked for you)</option>
                {events.map((ev) => (
                  <option key={ev.session_id} value={ev.session_id}>
                    {ev.event_name}, {formatDay(ev.starts_at)}
                    {ev.upcoming ? "" : " (past)"}
                  </option>
                ))}
              </Select>
            </label>
          )}
          {report.kind === "receivables" && (
            <label className="space-y-1">
              <span className={`block text-xs ${muted}`}>Due</span>
              <Select aria-label="Due" value={choices.due ?? ""} onChange={(e) => set({ due: e.target.value || undefined })} className="h-8 w-48">
                <option value="">Upcoming and past due</option>
                <option value="upcoming">Still upcoming</option>
                <option value="past_due">Past due</option>
              </Select>
            </label>
          )}
          <label className="space-y-1">
            <span className={`block text-xs ${muted}`}>Brand</span>
            <Select aria-label="Brand" value={choices.brand_id ?? 0} onChange={(e) => set({ brand_id: Number(e.target.value) || undefined })} className="h-8 w-40">
              <option value={0}>All brands</option>
              {BRANDS.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </Select>
          </label>
          {report.kind !== "email_stats" && (
            <label className="space-y-1">
              <span className={`block text-xs ${muted}`}>{isList ? "Also in" : "Only people in"}</span>
              <Select aria-label="Contact list" value={choices.list ?? ""} onChange={(e) => set({ list: e.target.value || undefined })} className="h-8 w-56">
                <option value="">Everyone</option>
                {segments.length > 0 && (
                  <optgroup label="Segments">
                    {segments.map((s) => (
                      <option key={s.id} value={`segment:${s.id}`}>
                        {s.name}
                      </option>
                    ))}
                  </optgroup>
                )}
                {searches.length > 0 && (
                  <optgroup label="Saved contact searches">
                    {searches.map((s) => (
                      <option key={s.id} value={`search:${s.id}`}>
                        {s.name}
                      </option>
                    ))}
                  </optgroup>
                )}
                <optgroup label="Tags">
                  {(tags.data ?? []).map((t) => (
                    <option key={t.id} value={`tag:${t.id}`}>
                      {t.name}
                    </option>
                  ))}
                </optgroup>
              </Select>
            </label>
          )}
          <div className="ml-auto flex flex-wrap items-end gap-2">
            {!isList && (
              <label className="space-y-1">
                <span className={`block text-xs ${muted}`}>On the home page</span>
                <Select aria-label="On the home page" value={report.home_group ?? "none"} disabled={home.isPending} onChange={(e) => home.mutate(e.target.value as "sales" | "company" | "none")} className="h-8 w-48">
                  <option value="none">Not shown</option>
                  <option value="sales">Sales and receivables</option>
                  <option value="company">Company and referral sales</option>
                </Select>
              </label>
            )}
            <Button size="sm" variant="outline" onClick={download} disabled={!r || !customReady}>
              Download CSV
            </Button>
          </div>
          {home.isError && <p className="w-full text-xs text-[var(--color-danger-fg)]">Couldn't change the home page: {(home.error as Error).message}</p>}
          {exporting && <p className={`w-full text-xs ${muted}`}>{exporting}</p>}
        </CardContent>
      </Card>

      {!customReady && <p className={`text-sm ${muted}`}>Pick both dates.</p>}
      {run.isError && <p className="text-sm text-[var(--color-danger-fg)]">Couldn't run the report: {(run.error as Error).message}</p>}
      {run.isLoading && <p className={`text-sm ${muted}`}>Running…</p>}
      {r && (
        <>
          <div className={`text-sm ${muted}`} data-testid="run-window">
            {r.window_label}
            {r.filters.length > 0 && <> · {r.filters.join(" · ")}</>}
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {r.totals.map((t) => (
              <Card key={t.key}>
                <CardContent className="py-3">
                  <div className={`text-xs ${muted}`}>{t.label}</div>
                  <div className="num text-xl font-semibold">{t.text}</div>
                </CardContent>
              </Card>
            ))}
          </div>
          {r.notes.map((n) => (
            <p key={n} className={`text-xs ${muted}`}>
              {n}
            </p>
          ))}
          {r.columns.length > 0 && (
            <Card className="overflow-x-auto">
              {r.rows.length === 0 ? (
                <p className={`px-4 py-3 text-sm ${muted}`}>Nothing in these dates.</p>
              ) : (
                <Table>
                  <THead>
                    <tr>
                      {r.columns.map((c) => (
                        <TH key={c.key} className={c.format === "money" || c.format === "count" || c.format === "percent" ? "text-right" : ""}>
                          {c.label}
                        </TH>
                      ))}
                    </tr>
                  </THead>
                  <tbody>
                    {r.rows.map((row, i) => (
                      <TR key={i}>
                        {r.columns.map((c) => (
                          <TD key={c.key} className={c.format === "money" || c.format === "count" || c.format === "percent" ? "num text-right" : c.format === "text" ? "max-w-[22rem] truncate" : "num whitespace-nowrap"}>
                            <CellValue col={c} row={row} />
                          </TD>
                        ))}
                      </TR>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
}

export function cellText(c: Column, v: unknown): string {
  if (v === null || v === undefined || v === "") return "";
  switch (c.format) {
    case "money":
      return formatCents(Number(v));
    case "count":
      return formatInt(Number(v));
    case "percent":
      return `${Number(v).toFixed(1)}%`;
    case "date":
      return formatDay(String(v));
    case "datetime":
      return formatWhen(String(v));
    default:
      return String(v);
  }
}

function CellValue({ col, row }: { col: Column; row: Record<string, unknown> }) {
  const text = cellText(col, row[col.key]);
  if (col.link === "contact" && row.contact_id) {
    return (
      <Link to="/contacts/$contactId" params={{ contactId: String(row.contact_id) }} className={link} title={col.format === "datetime" ? formatDateTime(String(row[col.key])) : undefined}>
        {text || `Contact ${row.contact_id}`}
      </Link>
    );
  }
  if (col.link === "order" && row.order_id) {
    return (
      <Link to="/billing/orders/$orderId" params={{ orderId: String(row.order_id) }} className={link}>
        {text || `Order ${row.order_id}`}
      </Link>
    );
  }
  return <>{text}</>;
}
